import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "audio-preview" }
const _hoisted_2 = ["title"]
const _hoisted_3 = {
  key: 1,
  class: "material-icons"
}
const _hoisted_4 = { class: "audio-body" }
const _hoisted_5 = ["max", "value", "disabled"]
const _hoisted_6 = { class: "time-row" }
const _hoisted_7 = { class: "time current-time" }
const _hoisted_8 = { class: "time duration" }
const _hoisted_9 = ["src"]

import { ref, toRefs, watch, nextTick } from 'vue';
import { EMIT } from '@/enum';
import ProgressSpinner from 'primevue/progressspinner';

interface Props {
  src: string | null;
  isForPreview?: boolean;
  isLoading?: boolean;
  metaDuration?: number | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AudioMessage',
  props: {
    src: { default: '' },
    isForPreview: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    metaDuration: { default: null }
  },
  setup(__props: any, { emit: __emit }) {

const props = __props;

const { src, isForPreview, metaDuration } = toRefs(props);

const emit = __emit;

const audioRef = ref<HTMLAudioElement | null>(null);
const isPlaying = ref<boolean>(false);
const duration = ref<number>(metaDuration?.value || 0);
const currentTime = ref<number>(0);
const pendingPlay = ref<boolean>(false);

const shouldRecalcDuration = () => metaDuration.value == null || metaDuration.value === 0;

const formatTime = (sec: number) => {
  const s = Math.floor((sec || 0) % 60)
    .toString()
    .padStart(2, '0');
  const m = Math.floor((sec || 0) / 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
};

const onLoadedMetadata = async () => {
  if (!shouldRecalcDuration()) return;
  const el = audioRef.value;
  if (!el) return;
  await readDurationSafe();
  currentTime.value = el.currentTime || 0;
};

const onDurationChange = () => {
  if (!shouldRecalcDuration()) return;
  const el = audioRef.value;
  if (!el) return;
  const d = el.duration;
  if (Number.isFinite(d) && d > 0) duration.value = d;
};

const onTimeUpdate = () => {
  const el = audioRef.value;
  if (!el) return;
  currentTime.value = el.currentTime || 0;
  isPlaying.value = !el.paused;
};

const onEnded = () => {
  isPlaying.value = false;
  pendingPlay.value = false;
};

const onSeeked = () => {
  const el = audioRef.value;
  if (!el) return;
  currentTime.value = el.currentTime || 0;
};

const onSeek = (e: Event) => {
  const el = audioRef.value;
  if (!el) return;
  const target = e.target as HTMLInputElement;
  const val = Number(target.value);
  const t = isNaN(val) ? 0 : Math.min(Math.max(0, val), duration.value || 0);
  el.currentTime = t;
  currentTime.value = t;
};

const togglePlay = async () => {
  const el = audioRef.value;
  if (!el) return;

  if (isForPreview.value && (!src.value || src.value === '')) {
    pendingPlay.value = true;
    emit(EMIT.PLAY);
    return;
  }

  if (el.paused) {
    try {
      await el.play();
      isPlaying.value = true;
      if (shouldRecalcDuration() && !duration.value) {
        setTimeout(() => readDurationSafe(1000, 100), 120);
      }
    } catch {
      pendingPlay.value = true;
    }
  } else {
    el.pause();
    isPlaying.value = false;
  }
};

const onCanPlayThrough = async () => {
  const el = audioRef.value;
  if (!el) return;

  if (shouldRecalcDuration() && !duration.value) await onLoadedMetadata();

  if (pendingPlay.value) {
    pendingPlay.value = false;
    try {
      await el.play();
      isPlaying.value = true;
    } catch {
      isPlaying.value = false;
      pendingPlay.value = true;
      console.error('Error playing audio');
    }
  }
};

const onCanPlay = async () => {
  if (!shouldRecalcDuration()) return;
  const el = audioRef.value;
  if (!el) return;
  if (!Number.isFinite(duration.value) || duration.value === 0) {
    await readDurationSafe();
  }
};

const readDurationSafe = async (maxMs = 1500, interval = 100) => {
  const el = audioRef.value;
  if (!el) return;
  const start = performance.now();
  while (performance.now() - start < maxMs) {
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) {
      duration.value = d;
      return;
    }
    await new Promise((r) => setTimeout(r, interval));
  }
  duration.value = Number.isFinite(el.duration) ? el.duration : 0;
};

watch(
  () => src.value,
  async (val) => {
    await nextTick();

    const el = audioRef.value;
    if (!el) return;

    el.load();
    currentTime.value = 0;

    if (val && pendingPlay.value) {
      pendingPlay.value = false;
      try {
        await el.play();
        isPlaying.value = true;
      } catch (err) {
        console.error('Error playing audio after source change', err);
        isPlaying.value = false;
        pendingPlay.value = true;
      }
    } else if (shouldRecalcDuration()) {
      await onLoadedMetadata();
    }
  },
  {
    immediate: true,
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!_unref(isForPreview))
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          class: "audio-control-icon control-icon",
          type: "button",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('discard')))
        }, [...(_cache[1] || (_cache[1] = [
          _createElementVNode("span", { class: "material-icons" }, "delete", -1)
        ]))]))
      : _createCommentVNode("", true),
    _createElementVNode("button", {
      class: "icon-btn audio-control-icon",
      type: "button",
      title: isPlaying.value ? 'Pause' : 'Play',
      onClick: togglePlay
    }, [
      (__props.isLoading)
        ? (_openBlock(), _createBlock(_unref(ProgressSpinner), { key: 0 }))
        : (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(isPlaying.value ? 'pause' : 'play_arrow'), 1))
    ], 8, _hoisted_2),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("input", {
        class: "seek",
        type: "range",
        min: "0",
        max: duration.value || 0,
        value: currentTime.value,
        step: "0.01",
        disabled: !duration.value,
        onInput: onSeek,
        onChange: onSeek
      }, null, 40, _hoisted_5),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("span", _hoisted_7, _toDisplayString(formatTime(currentTime.value)), 1),
        _createElementVNode("span", _hoisted_8, _toDisplayString(formatTime(duration.value)), 1)
      ])
    ]),
    _createElementVNode("audio", {
      ref_key: "audioRef",
      ref: audioRef,
      src: _unref(src),
      preload: "metadata",
      onLoadedmetadata: onLoadedMetadata,
      onCanplay: onCanPlay,
      onDurationchange: onDurationChange,
      onTimeupdate: onTimeUpdate,
      onSeeked: onSeeked,
      onCanplaythrough: onCanPlayThrough,
      onEnded: onEnded
    }, null, 40, _hoisted_9)
  ]))
}
}

})